import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import SelectAdvancedWithGrouping from 'common/SelectAdvancedWithGrouping/SelectAdvancedWithGrouping';
import AggregationTypePicker from 'components/lib/AggregationTypePicker';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';
import { ViewFilters } from 'components/View/types';
import { useAppSelector } from 'store/hook';
import type { ApartmentType } from 'types/accommodations';
import { isOverallApartmentType } from 'utils/data/apartmentTypes';

const StyledSelectAdvancedFilter = styled(SelectAdvancedWithGrouping)`
  && {
    height: 28px;
    color: var(--deep-blue);
    background-color: var(--lighter-gray);
    border-radius: 4px;
  }

  && .ant-select-selector {
    height: 28px;
    border-radius: 4px;
    border: none;
    box-shadow: none;
    background-color: transparent;
  }

  &&.active {
    background-color: var(--faded-blue);
  }

  &&.ant-select-focused .ant-select-selector {
    border: 1px solid var(--button-blue);
  }

  &&.ant-select-focused .ant-select-selection-item {
    color: var(--dark-blue);
  }

  && .ant-select-arrow {
    width: 16px;
    height: 16px;
    top: 12px;
  }

  && .ant-select-selection-placeholder {
    font-size: 12px;
    color: var(--deep-blue);
  }

  && .ant-select-selection-item {
    font-size: 12px;
    color: var(--dark-blue);
  }
`;

const FilterLabel = styled.span`
  font-family: var(--font-bold);
  color: var(--dark-blue);
`;

const FilterLabelBadge = styled.span`
  font-family: var(--font-bold);
  font-size: 10px;
  background-color: var(--positive-green);
  border-radius: 4px;
  padding: 3px 2px;
  margin-left: 4px;
  color: white;
`;

type Props = {
  roomTypes: any;
  priceDisplay?: number;
  onApply?: (next: { roomTypes: number[]; roomTypesPriceDisplay: number }) => void;
};

const RoomTypeFilter = ({ roomTypes, priceDisplay, onApply }: Props) => {
  const { apartmentTypes, viewFilters, setViewFilters } = useCommunityViewContext();
  const { apartmentTypeGroups } = useAppSelector((state) => state.global);
  const filters = viewFilters as ViewFilters;
  const committedPriceDisplay = priceDisplay ?? filters?.roomTypesPriceDisplay ?? AGGREGATION_TYPE.AVG;
  const [roomTypesPriceDisplay, setRoomTypesPriceDisplay] = useState<number>(committedPriceDisplay);

  const roomTypeGroupOptions = useMemo(() => {
    const availableIds = new Set((apartmentTypes ?? []).map((item: any) => item.id));
    const hasAvailableIds = availableIds.size > 0;

    const groups =
      apartmentTypeGroups
        ?.map((group) => {
          const variants =
            group?.apartmentTypes?.filter(
              (variant: ApartmentType) =>
                !isOverallApartmentType(variant) && (!hasAvailableIds || availableIds.has(variant.id))
            ) ?? [];
          if (!variants.length) {
            return null;
          }
          return {
            label: group.name,
            value: group.id,
            options: variants,
          };
        })
        .filter((group): group is { label: string; value: number; options: ApartmentType[] } => Boolean(group)) ?? [];

    if (groups.length > 0) {
      return groups;
    }

    return (apartmentTypes ?? [])
      .filter((item: any) => !isOverallApartmentType(item))
      .map((item: any) => ({
        label: item.type,
        value: item.id,
        options: [item],
      }));
  }, [apartmentTypeGroups, apartmentTypes]);

  const handleSelect = (values: number[]) => {
    if (onApply) {
      onApply({ roomTypes: values, roomTypesPriceDisplay });
      return;
    }
    setViewFilters((prevState: any) => ({
      ...prevState,
      roomTypes: values,
      roomTypesPriceDisplay: roomTypesPriceDisplay,
    }));
  };

  return (
    <StyledSelectAdvancedFilter
      className={classNames({ active: roomTypes && roomTypes.length > 0 })}
      disabled={!roomTypeGroupOptions.length}
      placeholder={'Room Type'}
      options={roomTypeGroupOptions}
      defaultValue={roomTypes ?? []}
      onSelect={handleSelect}
      dataTestId="room-type-filter"
      otherOptions={
        <AggregationTypePicker
          aggregationType={roomTypesPriceDisplay}
          label="PRICE DISPLAY"
          onSelect={setRoomTypesPriceDisplay}
        />
      }
      otherOptionsLabel={AGGREGATION_TYPE_LABELS[committedPriceDisplay]}
      labelRender={({ placeholderInfo, selectedItems, otherOptionsLabel }) => {
        let labelInfo = '';

        if (selectedItems.length > 0) {
          labelInfo = selectedItems[0].label;
        }

        if (otherOptionsLabel) {
          labelInfo = `${labelInfo} (${otherOptionsLabel})`;
        }

        return (
          <>
            <FilterLabel>{placeholderInfo} </FilterLabel>
            {labelInfo}
            {selectedItems.length > 1 && <FilterLabelBadge>+{selectedItems.length - 1}</FilterLabelBadge>}
          </>
        );
      }}
    />
  );
};

export default RoomTypeFilter;
